import React, { useEffect, useState } from "react";
import { X, Package, Sparkles, ShoppingCart, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

/**
 * Bundle-related shop UI:
 *   - BundleTicker:            top scrolling marquee (bundles + Essence highlights)
 *   - BundleOverlay:           opened when a customer clicks a bundle card in the ticker
 *   - BundleUpsellOverlay:     opened when a customer clicks an item that's part of a bundle
 *
 * All three share a compact, on-brand aesthetic and reuse the same
 * Add-Entire-Bundle action so audit trails stay consistent.
 */

// ---------- Ticker ----------

export function BundleTicker({ bundles, onOpenBundle }) {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    // Pull HQ news + upcoming approved events to weave into the ticker.
    api
      .get("/essence/ticker")
      .then((r) => {
        setNews(r.data?.news || []);
        setEvents(r.data?.events || []);
      })
      .catch(() => { setNews([]); setEvents([]); });
  }, []);

  // Sales items (bundles) form the base row; news + events are spread evenly
  // between them so no two highlights ever clump together.
  const sales = bundles.map((b) => ({ kind: "bundle", key: `b-${b.id}`, bundle: b }));
  const highlights = [
    ...news.map((n) => ({ kind: "essence", key: `n-${n.id}`, label: "News", body: n.body })),
    ...events.map((e) => ({ kind: "event", key: `ev-${e.id}`, label: "Event", body: e.body })),
  ];

  // Round-robin interleave: after each sales item, drop one highlight (if any),
  // cycling news/event alternation for even spread.
  const items = [];
  if (sales.length === 0 && highlights.length === 0) return null;
  const base = sales.length ? sales : highlights;
  const filler = sales.length ? highlights : [];
  const step = filler.length ? Math.max(1, Math.round(base.length / filler.length)) : 0;
  let fi = 0;
  base.forEach((s, i) => {
    items.push(s);
    if (step && (i + 1) % step === 0 && fi < filler.length) {
      items.push(filler[fi]);
      fi += 1;
    }
  });
  while (fi < filler.length) { items.push(filler[fi]); fi += 1; }
  if (!items.length) return null;

  // Duplicate the row so the CSS marquee loops seamlessly.
  const doubled = [...items, ...items];

  return (
    <div
      className="relative z-50 w-full overflow-hidden border-y-2 border-amber-500 bg-amber-200 py-2 shadow-sm"
      data-testid="bundle-ticker"
    >
      <div className="flex whitespace-nowrap animate-mpower-ticker">
        {doubled.map((it, i) => {
          if (it.kind === "bundle") {
            const b = it.bundle;
            const price = Number(b.bundle_price || 0).toFixed(2);
            return (
              <button
                key={`${it.key}-${i}`}
                onClick={() => onOpenBundle(b)}
                className="mx-4 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-amber-950 hover:text-amber-700 transition-colors"
                data-testid={`ticker-bundle-${b.id}`}
              >
                <Package size={14} className="text-amber-800" />
                <span className="uppercase tracking-wide">{b.name}</span>
                <span className="text-amber-900 font-bold tabular-nums">${price}</span>
                {b.savings > 0 && (
                  <span className="text-[10px] text-emerald-800 font-medium">
                    save ${Number(b.savings).toFixed(2)}
                  </span>
                )}
                <span className="text-amber-700/60">·</span>
              </button>
            );
          }
          const isEvent = it.kind === "event";
          return (
            <span
              key={`${it.key}-${i}`}
              className="mx-4 inline-flex items-center gap-2 text-xs sm:text-sm text-amber-950"
              data-testid={`ticker-${it.kind}`}
            >
              {isEvent ? <CalendarDays size={12} className="text-indigo-700" /> : <Sparkles size={12} className="text-rose-600" />}
              <span className={`uppercase tracking-wide text-[10px] font-semibold ${isEvent ? "text-indigo-800" : "text-rose-700"}`}>
                {it.label}
              </span>
              <span className="italic">{it.body}</span>
              <span className="text-amber-700/60">·</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Bundle detail overlay ----------

export function BundleOverlay({ bundle, onClose, onAddBundle }) {
  if (!bundle) return null;
  const price = Number(bundle.bundle_price || 0).toFixed(2);
  const orig = Number(bundle.original_total || 0).toFixed(2);
  const savings = Number(bundle.savings || 0).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="bundle-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-amber-300 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-amber-200 bg-gradient-to-br from-amber-500 to-amber-700 px-5 py-3 text-white">
          <div className="flex items-center gap-2">
            <Package size={18} />
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-100/90">Package Deal</div>
              <div className="text-base font-semibold">{bundle.name}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
            data-testid="bundle-overlay-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          <div className="mb-4 flex flex-wrap items-baseline gap-3">
            <div className="text-3xl font-bold tabular-nums text-neutral-900">${price}</div>
            {Number(orig) > Number(price) && (
              <div className="text-sm text-neutral-500 line-through tabular-nums">${orig}</div>
            )}
            {Number(savings) > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                save ${savings}
              </span>
            )}
          </div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
            Includes {bundle.products.length} item{bundle.products.length === 1 ? "" : "s"}
          </div>
          <ul className="space-y-2">
            {bundle.products.map((p) => (
              <li key={p.id} className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white p-2">
                <div className="h-12 w-12 flex-none overflow-hidden rounded-md bg-neutral-100">
                  {p.image_url && <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium capitalize text-neutral-800">{p.name}</div>
                  <div className="truncate font-mono text-[10px] text-neutral-400">{p.sku}</div>
                </div>
                <div className="text-sm tabular-nums text-neutral-500">${Number(p.retail_price || 0).toFixed(2)}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
          <Button variant="outline" onClick={onClose} data-testid="bundle-overlay-cancel">
            Close
          </Button>
          <Button
            className="bg-gradient-to-br from-amber-500 to-amber-700 text-white hover:from-amber-600 hover:to-amber-800"
            onClick={() => {
              onAddBundle(bundle);
              toast.success(`"${bundle.name}" added to cart · $${price}`);
              onClose();
            }}
            data-testid="bundle-overlay-add"
          >
            <ShoppingCart size={14} className="mr-1.5" /> Add Entire Bundle to Cart
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Upsell overlay (triggered from an individual product click) ----------

export function BundleUpsellOverlay({ product, bundle, onClose, onJustThisItem, onUpgradeToBundle }) {
  if (!product || !bundle) return null;
  const price = Number(bundle.bundle_price || 0).toFixed(2);
  const savings = Number(bundle.savings || 0).toFixed(2);
  const itemPrice = Number(product.retail_price || 0).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="bundle-upsell"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-amber-300 bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-amber-200 bg-gradient-to-br from-amber-500 to-amber-700 px-5 py-3 text-white">
          <Sparkles size={16} />
          <div className="text-sm font-semibold">Bundle & Save</div>
        </div>
        <div className="p-5 text-sm">
          <p className="text-neutral-700">
            <span className="font-semibold capitalize">{product.name}</span> is part of the{" "}
            <span className="font-semibold text-amber-700">{bundle.name}</span> package.
          </p>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-amber-800">Bundle price</span>
              <span className="text-lg font-bold tabular-nums text-amber-900">${price}</span>
            </div>
            {Number(savings) > 0 && (
              <div className="mt-1 text-xs text-emerald-700 font-medium">
                You save ${savings} vs buying separately
              </div>
            )}
            <div className="mt-2 text-xs text-neutral-600">
              Includes {bundle.products.length} item{bundle.products.length === 1 ? "" : "s"} — all shipped together.
            </div>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onJustThisItem} data-testid="upsell-just-item">
            No thanks, just this item (${itemPrice})
          </Button>
          <Button
            className="bg-gradient-to-br from-amber-500 to-amber-700 text-white hover:from-amber-600 hover:to-amber-800"
            onClick={onUpgradeToBundle}
            data-testid="upsell-upgrade"
          >
            Upgrade to Full Bundle <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
